import React, { useState } from "react";
import "./CapturePage.css";
import ExpeditionProgress from "cms/components/capture/ExpeditionProgress/ExpeditionProgress";
import HamburgerMenu from "cms/components/capture/HamburgerMenu/HamburgerMenu";
import ItemEditor from "cms/components/capture/ItemEditor/ItemEditor";
import ItemGroup from "cms/components/capture/ItemGroup/ItemGroup";
import { Button } from "@mui/material";
import { Modal } from "antd";

interface Expedition {
  id: number;
  name: string;
  status: "OK" | "Pending" | "Issue" | "Limbo";
  selected: boolean;
}

interface Item {
  id: number;
  title: { id: string; value: string };
  status: "OK" | "Pending" | "Issue" | "Limbo";
  type: "video" | "document" | "image";
  category: string;
}

const CapturePage: React.FC = () => {
  const [expeditions, setExpeditions] = useState<Expedition[]>([
    { id: 1, name: "1.1 States of Matter", status: "Pending", selected: false },
    { id: 2, name: "1.2 Solids", status: "Issue", selected: true },
    { id: 3, name: "1.3 Liquids", status: "OK", selected: false },
    { id: 4, name: "1.4 Gases", status: "Issue", selected: false },
  ]);

  const [items, setItems] = useState<Item[]>([
    {
      id: 1,
      title: { id: "1.2.2", value: "Molecular Arrangement" },
      status: "OK",
      type: "video",
      category: "Optional",
    },
    {
      id: 2,
      title: { id: "1.2.3", value: "Molecular Arrangement" },
      status: "OK",
      type: "document",
      category: "Compulsory",
    },
    {
      id: 3,
      title: { id: "1.2.4", value: "Thermal Properties" },
      status: "Pending",
      type: "image",
      category: "Compulsory",
    },
    {
      id: 4,
      title: { id: "1.2.5", value: "Mechanical Properties" },
      status: "Issue",
      type: "document",
      category: "Optional",
    },
    {
      id: 5,
      title: { id: "1.2.6", value: "Electrical Properties" },
      status: "OK",
      type: "video",
      category: "Optional",
    },
    {
      id: 6,
      title: { id: "1.2.7", value: "Quantum Properties" },
      status: "Pending",
      type: "image",
      category: "Compulsory",
    },
  ]);

  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    description: "",
  });

  const handleExpeditionSelect = (id: number) => {
    setExpeditions(
      expeditions.map((exp) => ({
        ...exp,
        selected: exp.id === id,
      }))
    );
  };

  const handleItemSelect = (id: number) => {
    const item = items.find((item) => item.id === id);
    if (item) setSelectedItem(item);
  };

  const handleSave = () => alert("Item saved!");

  const handleDelete = () => {
    setModalContent({
      title: "Confirm Deletion",
      description:
        "Are you sure you want to delete this item? This action cannot be undone.",
    });
    setModalOpen(true);
  };

  const handleFinalise = () => {
    setModalContent({
      title: "Confirm Finalisation",
      description: "Finalising will lock all changes. Proceed?",
    });
    setModalOpen(true);
  };

  const handleTitleChange = (newTitle: { id: string; value: string }) => {
    if (selectedItem) {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === selectedItem.id
            ? { ...item, title: newTitle } 
            : item
        )
      );
      setSelectedItem((prev) => (prev ? { ...prev, title: newTitle } : null)); 
    }
  };
  
  return (
    <div className="capture-page">
      <header className="capture-header">
        <div>
          <h1>Video List</h1>
          <h3>1.1 States of Matter</h3>
        </div>
        <div className="header-right">
          <div className="finalise-btn">
            <Button
              variant="contained"
              className="finalise"
              onClick={handleFinalise}
            >
              Finalise
            </Button>
          </div>
          <HamburgerMenu />
        </div>
      </header>

      <ExpeditionProgress
        expeditions={expeditions}
        onExpeditionSelect={handleExpeditionSelect}
      />

      <div className="state-matter-btn">
        <Button size="small" variant="contained" color="primary">
          Add
        </Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={handleDelete}
        >
          Delete
        </Button>
      </div>

      <div className="content">
        <section className="item-group-section">
          <ItemGroup items={items} onItemSelect={handleItemSelect} />
        </section>

        <section className={selectedItem ? "item-editor-section" : ""}>
          {selectedItem && (
            <ItemEditor
              title={selectedItem.title}
              status={selectedItem.status}
              onSave={handleSave}
              onDelete={handleDelete}
              onTitleChange={handleTitleChange}
            />
          )}
        </section>
      </div>

      <Modal
        className="confirm-changes-modal"
        centered
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setModalOpen(false)}
            className="cancel-btn"
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            onClick={() => setModalOpen(false)}
            className="confirm-btn"
          >
            Confirm
          </Button>,
        ]}
      >
        <div className="confirm-changes-modal-body">
          <div className="confirm-changes-modal-title">
            <h4>{modalContent.title}</h4>
          </div>
          <div className="confirm-changes-modal-dsc">
            <p className="dsc-text">{modalContent.description}</p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CapturePage;
